import React, { useState } from 'react';
import { AgentType } from '../types/models';

interface AgentFlowDiagramProps {
  highlightedAgent?: AgentType | null;
  onAgentHover?: (agent: AgentType | null) => void;
}

interface AgentNode {
  id: string;
  label: string;
  x: number;
  y: number;
  isOptional?: boolean;
  agentType: AgentType;
  tooltip: string;
}

interface AgentEdge {
  from: string;
  to: string;
  isFanOut?: boolean;
  isFanIn?: boolean;
}

const NODE_WIDTH = 90;
const NODE_HEIGHT = 26;
const GAP = 24;
const FAN_SPREAD = 28; // vertical spread for fan-out lines

// Positions: sequential flow with reducer cluster offset for fan-out visual
const COL = (i: number) => i * (NODE_WIDTH + GAP);

const NODES: AgentNode[] = [
  { id: 'orchestrator', label: 'Orchestrator', x: COL(0), y: 30, agentType: 'orchestrator', tooltip: 'First agent in the pipeline. Decides whether to block the query (non-legal), ask clarifying questions, or proceed with research.' },
  { id: 'researcher', label: 'Researcher', x: COL(1), y: 30, agentType: 'researcher', tooltip: 'Generates structured search parameters using multiple query styles to find relevant court opinions and passages.' },
  { id: 'relevancechecker', label: 'Relevance', x: COL(2), y: 30, isOptional: true, agentType: 'relevancechecker', tooltip: 'Optional. Filters out search results that are not actually relevant to the query. Controlled by RelevanceCheckEnabled config.' },
  { id: 'reducer', label: 'Reducer', x: COL(3), y: 30, isOptional: true, agentType: 'reducer', tooltip: 'Fan-out: processes each document in parallel (Task.WhenAll), then merges results back. Extracts key text ranges.' },
  { id: 'writer', label: 'Writer', x: COL(4), y: 30, agentType: 'writer', tooltip: 'Composes the final answer based on reduced/research results. Includes citations, confidence score, and acknowledges contrary authority.' },
];

const EDGES: AgentEdge[] = [
  { from: 'orchestrator', to: 'researcher' },
  { from: 'researcher', to: 'relevancechecker' },
  { from: 'relevancechecker', to: 'reducer', isFanOut: true },
  { from: 'reducer', to: 'writer', isFanIn: true },
];

const SVG_WIDTH = COL(4) + NODE_WIDTH + 20;
const SVG_HEIGHT = 86;

export const AgentFlowDiagram: React.FC<AgentFlowDiagramProps> = ({ highlightedAgent, onAgentHover }) => {
  const [localHover, setLocalHover] = useState<AgentType | null>(null);
  const activeHighlight = highlightedAgent || localHover;

  const isHighlighted = (node: AgentNode) => {
    if (!activeHighlight) return false;
    return node.agentType === activeHighlight;
  };

  const handleMouseEnter = (node: AgentNode) => {
    setLocalHover(node.agentType);
    onAgentHover?.(node.agentType);
  };

  const handleMouseLeave = () => {
    setLocalHover(null);
    onAgentHover?.(null);
  };

  return (
    <div data-component="AgentFlowDiagram" className="overflow-x-auto">
      <svg width={SVG_WIDTH} height={SVG_HEIGHT} className="block" style={{ minWidth: SVG_WIDTH }}>
        {/* Arrow markers */}
        <defs>
          <marker id="flow-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#9ca3af" />
          </marker>
          <marker id="flow-arrow-hl" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#7c3aed" />
          </marker>
          <marker id="flow-arrow-fan" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#f59e0b" />
          </marker>
        </defs>

        {/* Edges */}
        {EDGES.map((edge, i) => {
          const fromNode = NODES.find(n => n.id === edge.from)!;
          const toNode = NODES.find(n => n.id === edge.to)!;
          const highlighted = activeHighlight && (fromNode.agentType === activeHighlight || toNode.agentType === activeHighlight);

          const x1 = fromNode.x + NODE_WIDTH;
          const y1 = fromNode.y + NODE_HEIGHT / 2;
          const x2 = toNode.x;
          const y2 = toNode.y + NODE_HEIGHT / 2;

          if (edge.isFanOut) {
            return renderFanOutEdge(x1, y1, x2, y2, !!highlighted, i);
          }
          if (edge.isFanIn) {
            return renderFanInEdge(x1, y1, x2, y2, !!highlighted, i);
          }

          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={highlighted ? '#7c3aed' : '#9ca3af'}
              strokeWidth={highlighted ? 1.5 : 1}
              markerEnd={`url(#flow-arrow${highlighted ? '-hl' : ''})`}
            />
          );
        })}

        {/* Nodes */}
        {NODES.map(node => {
          const hl = isHighlighted(node);

          return (
            <g
              key={node.id}
              onMouseEnter={() => handleMouseEnter(node)}
              onMouseLeave={handleMouseLeave}
              className="cursor-help"
            >
              <title>{node.tooltip}</title>
              <rect
                x={node.x}
                y={node.y}
                width={NODE_WIDTH}
                height={NODE_HEIGHT}
                rx={6}
                fill={hl ? '#ede9fe' : (node.isOptional ? '#fffbeb' : '#f3f4f6')}
                stroke={hl ? '#7c3aed' : (node.isOptional ? '#f59e0b' : '#9ca3af')}
                strokeWidth={hl ? 2 : 1}
                strokeDasharray={node.isOptional ? '3 2' : undefined}
              />
              <text
                x={node.x + NODE_WIDTH / 2}
                y={node.y + NODE_HEIGHT / 2 + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={10}
                fontWeight={hl ? 600 : 400}
                fill={hl ? '#5b21b6' : '#374151'}
                pointerEvents="none"
              >
                {node.label}
              </text>
            </g>
          );
        })}

        {/* Fan-out/in annotation */}
        <text
          x={NODES[3].x + NODE_WIDTH / 2}
          y={NODES[3].y + NODE_HEIGHT + 14}
          textAnchor="middle"
          fontSize={8}
          fill="#92400e"
          fontStyle="italic"
        >
          parallel per doc
        </text>
      </svg>
    </div>
  );
};

/** Fan-out: single line splits into 3 parallel lines entering the target */
function renderFanOutEdge(x1: number, y1: number, x2: number, y2: number, highlighted: boolean, key: number) {
  const color = highlighted ? '#7c3aed' : '#f59e0b';
  const midX = x1 + (x2 - x1) * 0.5;
  const offsets = [-FAN_SPREAD, 0, FAN_SPREAD];

  return (
    <g key={key}>
      {offsets.map((offset, j) => (
        <path
          key={j}
          d={`M${x1},${y1} C${midX},${y1} ${midX},${y2 + offset} ${x2},${y2 + offset}`}
          fill="none"
          stroke={color}
          strokeWidth={highlighted ? 1.5 : 1}
          strokeDasharray={j !== 1 ? '2 2' : undefined}
          markerEnd={`url(#flow-arrow${highlighted ? '-hl' : '-fan'})`}
          opacity={j !== 1 ? 0.6 : 1}
        />
      ))}
    </g>
  );
}

/** Fan-in: 3 parallel lines merge from source into single target */
function renderFanInEdge(x1: number, y1: number, x2: number, y2: number, highlighted: boolean, key: number) {
  const color = highlighted ? '#7c3aed' : '#f59e0b';
  const midX = x1 + (x2 - x1) * 0.5;
  const offsets = [-FAN_SPREAD, 0, FAN_SPREAD];

  return (
    <g key={key}>
      {offsets.map((offset, j) => (
        <path
          key={j}
          d={`M${x1},${y1 + offset} C${midX},${y1 + offset} ${midX},${y2} ${x2},${y2}`}
          fill="none"
          stroke={color}
          strokeWidth={highlighted ? 1.5 : 1}
          strokeDasharray={j !== 1 ? '2 2' : undefined}
          markerEnd={`url(#flow-arrow${highlighted ? '-hl' : '-fan'})`}
          opacity={j !== 1 ? 0.6 : 1}
        />
      ))}
    </g>
  );
}
